import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LogOut, LayoutDashboard, Settings as SettingsIcon, Search, HelpCircle, Wrench, Home, Users, UserSquare2, Activity } from "lucide-react";
import { Button } from "./ui/button";
import ViewAsClientSwitcher from "./ViewAsClientSwitcher";

const LOGO_URL = "/tt-logo-white.png";

export function TTLogoMark({ size = 40, className = "" }) {
  return (
    <img
      src={LOGO_URL}
      alt="Thick & Thin"
      width={size}
      height={size}
      className={`object-contain ${className}`}
    />
  );
}

export default function TopBar({ activeClientName, activeClientLogo }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isTeam = user && (user.role === "admin" || user.role === "team" || user.role === "account_manager" || user.role === "accountant");
  const isAdmin = user?.role === "admin";
  const isAdminOrAM = user?.role === "admin" || user?.role === "account_manager";

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const home = isTeam ? (isAdmin ? "/home" : "/clients") : (user?.client_id ? `/clients/${user.client_id}` : "/");

  return (
    <header
      data-testid="top-bar"
      className="sticky top-0 z-40 flex items-center justify-between h-16 sm:h-24 px-2 sm:px-5 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-zinc-900"
    >
      <div className="flex items-center gap-2 sm:gap-5 min-w-0">
        <Link to={home} className="flex items-center group shrink-0" data-testid="brand-link" title="Thick & Thin">
          <TTLogoMark size={48} className="sm:hidden" />
          <TTLogoMark size={96} className="hidden sm:block" />
        </Link>
        {activeClientLogo ? (
          <div className="hidden md:flex items-center pl-5 border-l border-zinc-900 min-w-0">
            <img src={activeClientLogo} alt={activeClientName || ""}
              className="h-9 object-contain" data-testid="active-client-logo" />
          </div>
        ) : activeClientName && (
          <div className="hidden sm:flex items-center pl-3 sm:pl-5 border-l border-zinc-900 min-w-0 max-w-[200px] md:max-w-none">
            <span className="font-display text-[13px] sm:text-[15px] font-semibold text-zinc-100 truncate" data-testid="active-client-name">{activeClientName}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        <div className="hidden lg:block text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 mr-3">
          {new Date().toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
        </div>
        {isTeam && (
          <>
            {isAdmin && (
              <Button asChild variant="ghost" size="sm" className="text-zinc-400 hover:text-white text-[12px] h-8 px-2 sm:px-3" data-testid="nav-home">
                <Link to="/home" title="Home"><Home className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden lg:inline">Home</span></Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="sm" className="text-zinc-400 hover:text-white text-[12px] h-8 px-2 sm:px-3" data-testid="nav-clients">
              <Link to="/clients" title="Clients"><Users className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden lg:inline">Clients</span></Link>
            </Button>
            {isAdmin && (
              <Button asChild variant="ghost" size="sm" className="text-zinc-400 hover:text-white text-[12px] h-8 px-2 sm:px-3" data-testid="nav-team">
                <Link to="/team" title="Team"><UserSquare2 className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden lg:inline">Team</span></Link>
              </Button>
            )}
            {(isAdmin || user?.role === "accountant") && (
              <Button asChild variant="ghost" size="sm" className="text-zinc-400 hover:text-white text-[12px] h-8 px-2 sm:px-3" data-testid="nav-kpis">
                <Link to="/admin/kpis" title="KPIs"><Activity className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden lg:inline">KPIs</span></Link>
              </Button>
            )}
            {isAdminOrAM && (
              <Button asChild variant="ghost" size="sm" className="text-zinc-400 hover:text-white text-[12px] h-8 px-2 sm:px-3" data-testid="nav-cadences">
                <Link to="/cadences" title="Cadences"><Wrench className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden lg:inline">Cadences</span></Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="sm" className="text-zinc-400 hover:text-white text-[12px] h-8 px-2 sm:px-3" data-testid="nav-settings">
              <Link to="/settings"><SettingsIcon className="w-3.5 h-3.5 sm:mr-1.5" /><span className="hidden lg:inline">Settings</span></Link>
            </Button>
          </>
        )}
        {user && (
          <>
            <button
              onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
              className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-200 bg-zinc-900/60 border border-zinc-800 rounded-md h-8 px-2.5"
              data-testid="topbar-search-btn"
              title="Search (⌘K)">
              <Search className="w-3 h-3" />
              <span className="hidden md:inline">Search</span>
              <span className="hidden md:inline font-mono text-[9px] text-zinc-600 border border-zinc-800 rounded px-1 py-0.5 ml-1">⌘K</span>
            </button>
            <Button onClick={() => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))} variant="ghost" size="sm" className="sm:hidden text-zinc-500 h-8 w-8 p-0" title="Search">
              <Search className="w-3.5 h-3.5" />
            </Button>
            <ViewAsClientSwitcher />
            <div className="hidden md:flex items-center gap-2 pl-3 ml-2 border-l border-zinc-900">
              <div className="w-6 h-6 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center font-display font-semibold text-[10px] uppercase">{user.name?.[0] || "U"}</div>
              <div className="leading-tight">
                <div className="text-[11px] text-zinc-200" data-testid="user-name">{user.name}</div>
                <div className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono">{user.role}</div>
              </div>
            </div>
            <Button onClick={() => window.dispatchEvent(new CustomEvent("tt-relaunch-tutorial"))}
              variant="ghost" size="sm" data-testid="relaunch-tutorial-btn"
              className="text-zinc-500 hover:text-white h-8 w-8 p-0" title="Replay tour">
              <HelpCircle className="w-3.5 h-3.5" />
            </Button>
            <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="logout-btn" className="text-zinc-500 hover:text-white h-8 w-8 p-0">
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>
    </header>
  );
}
